import type { ComponentProps, ReactElement, ReactNode } from "react";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Best-practice delay (ms) before a tooltip opens. The tooltip does not pop up
 * instantly, so it doesn't distract on elements whose purpose is obvious at a
 * glance. This is the value of the shared root `<TooltipProvider>` mounted in
 * `App.tsx`, which every {@link SimpleTooltip} inherits unless it overrides
 * `delay`. The provider also enables Base UI's "skip delay" grouping, so moving
 * between adjacent tooltips shows them instantly.
 */
export const TOOLTIP_DELAY = 600;

type Props = {
	/** Tooltip body. When empty/nullish, the child renders without a tooltip. */
	content: ReactNode;
	/** Exactly one element the tooltip attaches to (Button, span, …). */
	children: ReactElement;
	side?: ComponentProps<typeof TooltipContent>["side"];
	/**
	 * ms before opening. Omit to inherit the shared {@link TOOLTIP_DELAY} from the
	 * root provider (and its hover-grouping). Pass `0` for logos/decorative icons
	 * that should label instantly. Base UI configures the delay on the provider,
	 * not the root, so an explicit value is applied via a small local provider.
	 */
	delay?: number;
};

/**
 * App-styled tooltip — the single replacement for the native `title=` HTML
 * attribute, which opens late and is rendered by the browser. Wraps the
 * canonical Base UI tooltip from `@/components/ui/tooltip`.
 *
 * Accessibility: the tooltip links to the trigger via `aria-describedby`, NOT
 * as the accessible name — keep/add an `aria-label` on icon-only triggers.
 */
export const SimpleTooltip = ({ content, children, side, delay }: Props) => {
	if (content === null || content === undefined || content === "") {
		return children;
	}

	const tooltip = (
		<Tooltip>
			<TooltipTrigger render={children} />
			<TooltipContent side={side}>{content}</TooltipContent>
		</Tooltip>
	);

	// The common case inherits the app's shared root provider (delay + grouping).
	// Only an explicit per-instance delay needs its own provider.
	return delay === undefined ? (
		tooltip
	) : (
		<TooltipProvider delay={delay}>{tooltip}</TooltipProvider>
	);
};
